// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - Create Org Trail Session Content Assignment

import React, {
    useEffect,
    useMemo,
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useGetSessionReferenceContentQuery,
} from "@/pages/admin/sessionReferenceContent/sessionReferenceContentApiSlice"

import {
    useGetOrgTrailSessionContentAssignmentsQuery,
    useCreateOrgTrailSessionContentAssignmentMutation,
} from "./orgTrailSessionContentAssignmentsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value => {

    if (!value) return ""

    if (typeof value === "string") return value

    return value?._id || value?.id || ""
}

const toList = data =>
    data?.ids?.map(id => data.entities[id]).filter(Boolean) || []


/*
|--------------------------------------------------------------------------
| 02 - Initial State
|--------------------------------------------------------------------------
*/

const initialFormState = {
    orgId: "",
    trailId: "",
    cohortId: "",
    sessionId: "",
    referenceContentId: "",
    active: true,
}


/*
|--------------------------------------------------------------------------
| 03 - Main Component
|--------------------------------------------------------------------------
*/

const EtCreateOrgTrailSessionContentAssignment = ({
    setActiveAdminView,
}) => {

    useTitle("Create Session Content Assignment")

    const navigate = useNavigate()

    const [formData, setFormData] =
        useState(initialFormState)


    /*
    |--------------------------------------------------------------------------
    | 04 - Create Mutation
    |--------------------------------------------------------------------------
    */

    const [
        createOrgTrailSessionContentAssignment,
        {
            isLoading,
            isSuccess,
            isError,
            error,
        },
    ] = useCreateOrgTrailSessionContentAssignmentMutation()


    /*
    |--------------------------------------------------------------------------
    | 05 - Master Queries
    |--------------------------------------------------------------------------
    */

    const {
        data: organizationsData,
    } = useGetOrganizationsQuery(
        "organizationsList"
    )

    const {
        data: trailsData,
    } = useGetTrailsQuery(
        "trailsList"
    )

    const {
        data: sessionsData,
    } = useGetSessionsQuery(
        "sessionsList"
    )

    const {
        data: cohortsData,
    } = useGetOrgTrailSessionCohortsQuery({
        active: true,
    })

    const {
        data: referenceContentData,
    } = useGetSessionReferenceContentQuery({
        active: true,
    })

    const {
        data: existingAssignmentsData,
    } = useGetOrgTrailSessionContentAssignmentsQuery()


    /*
    |--------------------------------------------------------------------------
    | 06 - Normalize Lists
    |--------------------------------------------------------------------------
    */

    const organizationList = useMemo(
        () => toList(organizationsData),
        [organizationsData]
    )

    const trailList = useMemo(
        () => toList(trailsData),
        [trailsData]
    )

    const sessionList = useMemo(
        () => toList(sessionsData),
        [sessionsData]
    )

    const cohortList = useMemo(
        () => toList(cohortsData),
        [cohortsData]
    )

    const referenceContentList = useMemo(
        () => toList(referenceContentData),
        [referenceContentData]
    )

    const existingAssignmentList = useMemo(
        () => toList(existingAssignmentsData),
        [existingAssignmentsData]
    )


    /*
    |--------------------------------------------------------------------------
    | 07 - Filtered Cohorts
    |--------------------------------------------------------------------------
    */

    const filteredCohorts = useMemo(() => {

        if (
            !formData.orgId ||
            !formData.trailId
        ) {
            return []
        }

        return cohortList
            .filter(cohort =>
                getRefId(cohort.orgId) === formData.orgId &&
                getRefId(cohort.trailId) === formData.trailId &&
                cohort.active !== false
            )
            .sort(
                (a, b) =>
                    (a.cohortSequence ?? 999999) -
                    (b.cohortSequence ?? 999999)
            )

    }, [
        cohortList,
        formData.orgId,
        formData.trailId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 08 - Filtered Sessions
    |--------------------------------------------------------------------------
    */

    const filteredSessions = useMemo(() => {

        if (!formData.trailId) return []

        return sessionList
            .filter(session =>
                getRefId(session.trailId) === formData.trailId &&
                session.active !== false
            )
            .sort(
                (a, b) =>
                    (a.sessionSequence ?? 999999) -
                    (b.sessionSequence ?? 999999)
            )

    }, [
        sessionList,
        formData.trailId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 09 - Reference Content For Selected Session
    |--------------------------------------------------------------------------
    */

    const filteredReferenceContent = useMemo(() => {

        if (!formData.sessionId) return []

        return referenceContentList
            .filter(content =>
                getRefId(content.sessionId) === formData.sessionId &&
                content.active !== false
            )
            .sort(
                (a, b) =>
                    (a.displayOrder ?? 0) -
                    (b.displayOrder ?? 0)
            )

    }, [
        referenceContentList,
        formData.sessionId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 10 - Existing Assignment Match
    |--------------------------------------------------------------------------
    */

    const matchingExistingAssignment = useMemo(() => {

        if (
            !formData.orgId ||
            !formData.trailId ||
            !formData.cohortId ||
            !formData.sessionId ||
            !formData.referenceContentId
        ) {
            return null
        }

        return existingAssignmentList.find(
            assignment =>

                getRefId(assignment.orgId) === formData.orgId &&

                getRefId(assignment.trailId) === formData.trailId &&

                getRefId(assignment.cohortId) === formData.cohortId &&

                getRefId(assignment.sessionId) === formData.sessionId &&

                getRefId(assignment.referenceContentId) ===
                    formData.referenceContentId

        ) || null

    }, [
        existingAssignmentList,
        formData,
    ])


    /*
    |--------------------------------------------------------------------------
    | 11 - Return To List
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!isSuccess) return

        setFormData(initialFormState)

        if (setActiveAdminView) {

            setActiveAdminView(
                "orgTrailSessionContentAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionContentAssignments"
            )

        }

    }, [
        isSuccess,
        navigate,
        setActiveAdminView,
    ])


    /*
    |--------------------------------------------------------------------------
    | 12 - Field Change
    |--------------------------------------------------------------------------
    */

    const handleChange = e => {

        const {
            name,
            value,
            type,
            checked,
        } = e.target

        if (type === "checkbox") {

            setFormData(prev => ({
                ...prev,
                [name]: checked,
            }))

            return
        }

        setFormData(prev => ({

            ...prev,

            [name]: value,

            ...(name === "orgId"
                ? {
                    trailId: "",
                    cohortId: "",
                    sessionId: "",
                    referenceContentId: "",
                }
                : {}),

            ...(name === "trailId"
                ? {
                    cohortId: "",
                    sessionId: "",
                    referenceContentId: "",
                }
                : {}),

            ...(name === "sessionId"
                ? {
                    referenceContentId: "",
                }
                : {}),

        }))
    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async e => {

        e.preventDefault()

        if (
            !formData.orgId ||
            !formData.trailId ||
            !formData.cohortId ||
            !formData.sessionId ||
            !formData.referenceContentId
        ) {

            alert(
                "Organization, Trail, Cohort, Session and Reference Content are required."
            )

            return
        }

        if (matchingExistingAssignment) {

            alert(
                "This Reference Content is already assigned to the selected cohort. Use Update instead."
            )

            return
        }

        try {

            await createOrgTrailSessionContentAssignment({
                ...formData,
            }).unwrap()

            alert(
                "Session Content assignment created successfully"
            )

        } catch (submitError) {

            console.error(submitError)

            alert(
                submitError?.data?.message ||
                "Failed to create Session Content assignment"
            )

        }
    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Cancel / Clear
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {

        if (setActiveAdminView) {

            setActiveAdminView(
                "orgTrailSessionContentAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionContentAssignments"
            )

        }
    }

    const handleClear = () => {
        setFormData(initialFormState)
    }


    /*
    |--------------------------------------------------------------------------
    | 15 - Reusable Selector
    |--------------------------------------------------------------------------
    */

    const renderSelector = ({
        label,
        name,
        options,
        disabled = false,
        placeholder,
        getLabel,
    }) => (

        <div className="bg-white rounded m-1 p-2">

            <p className="font-bold text-xs m-1">
                {label}
            </p>

            <select
                name={name}
                value={formData[name]}
                onChange={handleChange}
                disabled={disabled}
                className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
            >

                <option value="">
                    {placeholder}
                </option>

                {options.map(item => (

                    <option
                        key={getRefId(item)}
                        value={getRefId(item)}
                    >
                        {getLabel(item)}
                    </option>

                ))}

            </select>

        </div>
    )


    /*
    |--------------------------------------------------------------------------
    | 16 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">

            {isLoading && (
                <PulseLoader color="#ea580c" />
            )}

            {isError && (

                <p className="text-orange-700 py-2">
                    {error?.data?.message}
                </p>

            )}

            <div className="bg-white p-1 m-1 rounded">

                <p className="p-1 m-1 font-bold font-poppins text-xl">
                    Add a Session Content Assignment
                </p>

                <div className="bg-gray-200 rounded m-1 p-1">

                    <form
                        onSubmit={handleSubmit}
                        className="m-1 p-1 text-xs font-poppins"
                    >

                        {/* Organization */}

                        {renderSelector({
                            label: "Organization",
                            name: "orgId",
                            options: organizationList,
                            placeholder: "Select Organization",
                            getLabel: org => org.organizationName,
                        })}


                        {/* Trail */}

                        {renderSelector({
                            label: "Trail",
                            name: "trailId",
                            options: trailList,
                            disabled: !formData.orgId,
                            placeholder: "Select Trail",
                            getLabel: trail => trail.trailName,
                        })}


                        {/* Cohort */}

                        {renderSelector({
                            label: "Cohort",
                            name: "cohortId",
                            options: filteredCohorts,
                            disabled:
                                !formData.orgId ||
                                !formData.trailId,
                            placeholder: "Select Cohort",
                            getLabel: cohort =>
                                cohort.cohortName ||
                                cohort.cohortCode,
                        })}


                        {/* Session */}

                        {renderSelector({
                            label: "Session",
                            name: "sessionId",
                            options: filteredSessions,
                            disabled: !formData.trailId,
                            placeholder: "Select Session",
                            getLabel: session =>
                                `${session.sessionSequence
                                    ? `${session.sessionSequence} - `
                                    : ""}${session.sessionName}`,
                        })}


                        {/* Reference Content */}

                        {renderSelector({
                            label: "Session Reference Content",
                            name: "referenceContentId",
                            options: filteredReferenceContent,
                            disabled: !formData.sessionId,
                            placeholder: "Select Reference Content",
                            getLabel: content =>
                                `${content.displayOrder
                                    ? `${content.displayOrder} - `
                                    : ""}${content.title} (${content.contentType})`,
                        })}


                        {formData.sessionId &&
                            filteredReferenceContent.length === 0 && (

                                <div className="bg-orange-50 rounded m-1 p-2">
                                    <p className="text-[10px] text-orange-700">
                                        No active reference content is available
                                        for this Session. Create content in
                                        the master library first.
                                    </p>
                                </div>

                            )}


                        {matchingExistingAssignment && (

                            <div className="bg-orange-50 rounded m-1 p-2">
                                <p className="text-[10px] text-orange-700">
                                    This content is already assigned to this
                                    cohort. Use Update instead.
                                </p>
                            </div>

                        )}


                        {/* Active */}

                        <div className="bg-white rounded m-1 p-2">

                            <label className="flex items-center bg-gray-100 rounded m-1 p-2">

                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                    className="m-1"
                                />

                                <span className="text-xs m-1">
                                    Active
                                </span>

                            </label>

                        </div>


                        {/* Actions */}

                        <div className="bg-white m-1 px-1 py-3 rounded">

                            <EtBtnFormBasic
                                name="Create"
                                type="create"
                                handleClick={handleSubmit}
                            />

                            <EtBtnFormBasic
                                name="Clear"
                                type="clear"
                                handleClick={handleClear}
                            />

                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={handleCancel}
                            />

                        </div>

                    </form>

                </div>

            </div>

        </div>
    )
}


export default EtCreateOrgTrailSessionContentAssignment